import axios from 'axios';
import { API_BASE_URL } from '@config';
import * as ActionsDialog from 'app/store/actions';
import { showMessage } from 'app/store/actions/fuse';

// SET


export const SET_TRAININGS_LIST = 'SET_TRAININGS_LIST';
export const setTrainingList = trainings => ({
	type: SET_TRAININGS_LIST,
	payload: trainings
});

export const SET_TRAINING = 'SET_TRAINING';
export const setTraining = training => ({
	type: SET_TRAINING,
	payload: training
});

export const SET_REGISTRATIONS = 'SET_REGISTRATIONS';
export const setRegistrations = registrations => ({
	type: SET_REGISTRATIONS,
	payload: registrations
});

export const SET_PARTICIPANT_SESSION = 'SET_PARTICIPANT_SESSION';
export const setParticipantSession = session => ({
	type: SET_PARTICIPANT_SESSION,
	payload: session
});

export const SET_SELECTED = 'SET_SELECTED';
export const setSelected = training => ({
	type: SET_SELECTED,
	payload: training
});

export const SET_SEARCH_TEXT = 'SET_SEARCH_TEXT';
export const setSearchText = event => ({
	type: SET_SEARCH_TEXT,
	searchText: event.target.value
});

export const SET_TRAINING_ERROR = 'SET_TRAINING_ERROR';
export const setTrainingError = error => ({
	type: SET_TRAINING_ERROR,
	payload: error
});

// CLEAR
export const CLEAR_ERROR = 'CLEAR_ERROR';
export const clearError = () => ({
	type: CLEAR_ERROR
});

export const CLEAR_TRAINING = 'CLEAR_TRAINING';
export const clearTraining = () => ({
	type: CLEAR_TRAINING
});

export const CLEAR_TRAININGS_REDUCER = 'CLEAR_TRAININGS_REDUCER';
export const clearTrainingsReducer = () => ({
	type: CLEAR_TRAININGS_REDUCER
});

// * ////////////// HEADERS ////////////// * //
const headers = {
	headers: {
		admin: true
	}
};

// * ////////////// NEW ////////////// * //

export const newTraining = () => dispatch => {
	const data = {
		name: '',
		description: '',
		provider: '',
		data: {}
	};
	dispatch(setTraining(data));
};

// * ////////////// GET ////////////// * //

export const getTrainings = () => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/trainings/`);
	return request
		.then(response => dispatch(setTrainingList(response.data)))
		.catch(err => dispatch(setTrainingError({ ...err.data, type: 'modal' })));
};

export const getTraining = (trainingId) => async dispatch => {
	try {
		const response = await axios.get(`${API_BASE_URL}/api/trainings/${trainingId}`);
		dispatch({
			type: SET_TRAINING,
			payload: response.data
		});
		return response.data;
	} catch (error) {
		console.error('Error fetching workshop:', error);
		throw error;
	}
};

export const getRegistrations = trainingId => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/trainings/${trainingId}/registrations`, headers);
	return request
		.then(response => {
			dispatch(setRegistrations(response.data));
		})
		.catch(err => {
			dispatch(setTrainingError(err.data));
		});
}

/**
 * Get active VNC users for a training session
 */
export const getActiveVncUsers = (trainingId) => async dispatch => {
	try {
		const response = await axios.get(`${API_BASE_URL}/api/trainings/${trainingId}/vnc/active-users`, headers);
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error fetching active VNC users:', error);
		const errorMessage = error.response?.data?.error || 'Failed to fetch active VNC users';
		return { success: false, error: errorMessage };
	}
};

/**
 * Fetch the latest server-stored desktop snapshot JPEG for a workshop
 * participant. Returns an object URL the caller can drop into an
 * <img src=...>. The caller owns the URL and must revoke it via
 * URL.revokeObjectURL on unmount / refresh to avoid leaking blobs.
 *
 * Returns { success: false, notFound: true } when the server has no
 * snapshot yet (HTTP 404). That's the common case for a student who
 * just opened their desktop tab; the observer tile then falls back
 * to the iframe's own dark background until heidi-snapshot.js
 * uploads the first frame.
 */
export const getDesktopSnapshotObjectUrl = (trainingId, username) => async dispatch => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/api/trainings/${trainingId}/desktop-snapshots/${encodeURIComponent(username)}`,
			{ responseType: 'blob' }
		);
		const objectUrl = URL.createObjectURL(response.data);
		return { success: true, objectUrl };
	} catch (error) {
		if (error.response && error.response.status === 404) {
			return { success: false, notFound: true };
		}
		return { success: false, error: error.message || 'snapshot_fetch_failed' };
	}
};

/**
 * Instructor desktop take-control coordination.
 *
 * When the instructor enters Take Control on a Heidi Desktop tile, we
 * register that (clusterId, username) is under instructor control so
 * the student's heidi-reconnect can swap its "Session opened in
 * another tab" overlay for a non-blocking "Instructor is controlling
 * your session" message - and importantly NOT offer the student a
 * Reconnect button that would kick the instructor back out.
 *
 * Three calls:
 *   - take: POST  /api/desktop-control            { clusterId, username }
 *   - heartbeat: POST /api/desktop-control/heartbeat (every 30s while
 *     in control so a crashed instructor tab times out within 2 min)
 *   - release: DELETE /api/desktop-control        { clusterId, username }
 */
export const setInstructorDesktopControl = (clusterId, username, action) => async () => {
	const url = `${API_BASE_URL}/api/desktop-control${action === 'heartbeat' ? '/heartbeat' : ''}`;
	try {
		if (action === 'release') {
			await axios.delete(url, { data: { clusterId, username } });
		} else {
			await axios.post(url, { clusterId, username });
		}
		return { success: true };
	} catch (error) {
		// Stale-prune is server-side - failures here are best-effort.
		return { success: false, error: error.message };
	}
};

/**
 * Get a DCV auth token for the teacher to view/control a student's DCV session.
 * Used by the Desktop Observer "Take Control" feature for DCV sessions.
 */
export const getDcvAuthToken = (trainingId, username, clusterId, dcvSessionId) => async dispatch => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/api/trainings/${trainingId}/dcv/auth-token`,
			{ username, clusterId, dcvSessionId },
			headers
		);
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error getting DCV auth token:', error);
		const errorMessage = error.response?.data?.message || 'Failed to get DCV auth token';
		return { success: false, error: errorMessage };
	}
};

/**
 * Terminate VNC session for a single user
 */
export const terminateUserVncSession = (username) => async dispatch => {
	try {
		const response = await axios.post(`${API_BASE_URL}/api/vnc/terminate-session`, { username }, headers);
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error terminating VNC session:', error);
		const errorMessage = error.response?.data?.message || 'Failed to terminate VNC session';
		return { success: false, error: errorMessage };
	}
};

/**
 * Get applications for a training session
 */
export const getTrainingApplications = (trainingId) => async dispatch => {
	try {
		const response = await axios.get(`${API_BASE_URL}/api/trainings/${trainingId}/applications`, headers);
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error fetching training applications:', error);
		const errorMessage = error.response?.data?.message || 'Failed to fetch training applications';
		return { success: false, error: errorMessage };
	}
};

/**
 * Add application to training session
 */
export const addApplicationToTraining = (trainingId, applicationData) => async dispatch => {
	try {
		const response = await axios.post(`${API_BASE_URL}/api/trainings/${trainingId}/applications`, applicationData, headers);
		dispatch(getTraining(trainingId)); // Refresh training data
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error adding application to training:', error);
		const errorMessage = error.response?.data?.message || 'Failed to add application to training';
		return { success: false, error: errorMessage };
	}
};

/**
 * Remove application from training session
 */
export const removeApplicationFromTraining = (trainingId, appId) => async dispatch => {
	try {
		const response = await axios.delete(`${API_BASE_URL}/api/trainings/${trainingId}/applications/${appId}`, headers);
		dispatch(getTraining(trainingId)); // Refresh training data
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error removing application from training:', error);
		const errorMessage = error.response?.data?.message || 'Failed to remove application from training';
		return { success: false, error: errorMessage };
	}
};

/**
 * Get courses for a training session
 */
export const getTrainingCourses = (trainingId) => async dispatch => {
	try {
		const response = await axios.get(`${API_BASE_URL}/api/trainings/${trainingId}/courses`, headers);
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error fetching training courses:', error);
		const errorMessage = error.response?.data?.message || 'Failed to fetch training courses';
		return { success: false, error: errorMessage };
	}
};

/**
 * Add course to training session
 */
export const addCourseToTraining = (trainingId, courseData) => async dispatch => {
	try {
		const response = await axios.post(`${API_BASE_URL}/api/trainings/${trainingId}/courses`, courseData, headers);
		dispatch(getTraining(trainingId)); // Refresh training data
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error adding course to training:', error);
		const errorMessage = error.response?.data?.message || 'Failed to add course to training';
		return { success: false, error: errorMessage };
	}
};

/**
 * Remove course from training session
 */
export const removeCourseFromTraining = (trainingId, courseId) => async dispatch => {
	try {
		const response = await axios.delete(`${API_BASE_URL}/api/trainings/${trainingId}/courses/${courseId}`, headers);
		dispatch(getTraining(trainingId)); // Refresh training data
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error removing course from training:', error);
		const errorMessage = error.response?.data?.message || 'Failed to remove course from training';
		return { success: false, error: errorMessage };
	}
};

// * ////////////// POST ////////////// * //

export const saveTraining = training => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.post(`${API_BASE_URL}/api/trainings/`, {
		...training
	}, headers);
	return request
		.then(() => {
			dispatch(ActionsDialog.closeDialog());
			dispatch(getTrainings());
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setTrainingError({ ...err.data, type: 'form' }));
		});
};

export const editTraining = training => dispatch => {
	
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/trainings/${training.id}`, training, headers);
	return request
		.then((response) => {
			
			// If the response contains the updated training, update it in the store immediately
			if (response.data.training) {
				dispatch({
					type: SET_TRAINING,
					payload: response.data.training
				});
			}
			
			dispatch(ActionsDialog.closeDialog());
			dispatch(getTrainings()); // Still refresh the full list for consistency
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			console.error('editTraining error:', err);
			dispatch(ActionsDialog.toggleLoading());
			if (err.response && err.response.data && err.response.data.code === 400) {
				dispatch(showMessage({ message: err.response.data.message, variant: 'error' }));
			} else if (err.data && err.data.code === 400) {
				dispatch(showMessage({ message: err.data.message, variant: 'error' }));
			}
		});
}

export const setToCluster = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.post(`${API_BASE_URL}/api/trainings/set`, data, headers);
	return request
		.then(() => {
			dispatch(ActionsDialog.closeDialog());
			dispatch(getTrainings());
			dispatch(ActionsDialog.toggleLoading());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setTrainingError({ ...err.data, type: 'modal' }));
		});
};

// * ////////////// PUT ////////////// * //

export const editCollaborators = data => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/trainings/collaborators`, data, headers);
	return request
		.then(res => {
			dispatch(setSelected(res.data.training));
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getTrainings());
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setTrainingError({ ...err.data, type: 'modal' }));
		});
};

// * ////////////// DELETE ////////////// * //

export const deleteTraining = (id, returnToTrainingsTable) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.delete(`${API_BASE_URL}/api/trainings/${id}`, headers);
	return request
		.then(() => dispatch(getTrainings()))
		.then(() => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(showMessage({ message: 'Workshop Deleted' }));
			returnToTrainingsTable();
		})
		.catch(err => {
			console.error(err);
			dispatch(ActionsDialog.toggleLoading());
			dispatch(showMessage({ ...err.data, variant: 'error' }));
		});
};

/**
 * Check if contact email functionality is available
 */
export const checkContactEmailAvailable = () => async dispatch => {
	try {
		const response = await axios.get(`${API_BASE_URL}/api/trainings/contact-email/available`, headers);
		return { success: true, available: response.data.available };
	} catch (error) {
		console.error('Error checking contact email availability:', error);
		return { success: false, available: false };
	}
};

/**
 * Send contact email to student
 */
export const sendContactEmailToStudent = (trainingId, studentEmail, message, teacherName) => async dispatch => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/api/trainings/${trainingId}/contact-student`, 
			{ studentEmail, message, teacherName },
			headers
		);
		return { success: true, message: response.data.message, studentName: response.data.studentName };
	} catch (error) {
		console.error('Error sending contact email:', error);
		const errorMessage = error.response?.data?.message || 'Failed to send contact email';
		return { success: false, error: errorMessage };
	}
};

/**
 * Get participant's training session data
 */
export const getParticipantSession = () => async dispatch => {
	try {
		// Get token from localStorage for JWT authentication
		const token = window.localStorage.getItem('token');
		const participantHeaders = {
			headers: {
				Authorization: `Bearer ${token}`
			}
		};
		
		const response = await axios.get(`${API_BASE_URL}/api/trainings/participant/session`, participantHeaders);
		dispatch(setParticipantSession(response.data));
		return { success: true, data: response.data };
	} catch (error) {
		console.error('Error fetching participant session:', error);
		const errorMessage = error.response?.data?.message || 'Failed to fetch training session';
		return { success: false, error: errorMessage };
	}
};
